import React from 'react';
import { cn } from './utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'glow' | 'wave' | 'multi';
  speed?: 'slow' | 'normal' | 'fast';
  wave?: number; // 1-6 for wave delay
}

/**
 * 기본 스켈레톤 컴포넌트
 */
function Skeleton({ className, variant = 'default', speed = 'normal', wave, id, style, ...props }: SkeletonProps) {
  // Linter (no-nested-ternary) 수정을 위해 switch 문으로 변경
  let speedClass;
  switch (speed) {
    case 'fast':
      speedClass = 'animate-shimmer-fast';
      break;
    case 'slow':
      speedClass = 'animate-shimmer-slow';
      break;
    default:
      speedClass = 'animate-shimmer';
  }

  let variantClass;
  switch (variant) {
    case 'glow':
      variantClass = 'shimmer-glow';
      break;
    case 'wave':
      variantClass = 'shimmer-wave';
      break;
    case 'multi':
      variantClass = 'shimmer-multi';
      break;
    default:
      variantClass = speedClass;
  }

  const waveClass = wave ? `animate-shimmer-wave-${wave}` : '';
  const baseClass = 'bg-muted rounded-md shimmer-container';

  return <div className={cn(baseClass, variantClass, waveClass, className)} id={id} style={style} />;
}

/**
 * 설문 카드 스켈레톤
 */
interface QuestionCardSkeletonProps {
  className?: string;
  showStats?: boolean;
  index?: number; // Wave 효과를 위한 인덱스
}

function QuestionCardSkeleton({ className, showStats = true, index = 0 }: QuestionCardSkeletonProps) {
  const waveIndex = (index % 6) + 1; // 1-6 순환

  return (
    <div className={cn('bg-white rounded-xl border border-border p-6 space-y-4 shimmer-container', className)}>
      {/* 헤더 영역 */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-16" variant="glow" wave={waveIndex} /> {/* 카테고리 배지 */}
          <Skeleton className="h-6 w-20" variant="wave" wave={waveIndex} /> {/* 시간 배지 */}
        </div>
        <div className="flex items-center gap-1">
          <Skeleton className="h-8 w-8 rounded" variant="multi" wave={waveIndex} />
          <Skeleton className="h-8 w-8 rounded" variant="multi" wave={waveIndex} />
          <Skeleton className="h-8 w-8 rounded" variant="multi" wave={waveIndex} />
        </div>
      </div>

      {/* 작성자 정보 */}
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" variant="glow" wave={waveIndex} />
        <div className="space-y-1">
          <Skeleton className="h-4 w-20" variant="default" wave={waveIndex} />
          <Skeleton className="h-3 w-16" variant="default" wave={waveIndex} />
        </div>
      </div>

      {/* 제목 */}
      <div className="space-y-2">
        <Skeleton className="h-6 w-4/5" variant="wave" wave={waveIndex} />
        <Skeleton className="h-6 w-3/5" variant="wave" wave={waveIndex} />
      </div>

      {/* 설명 (선택적) */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" variant="default" wave={waveIndex} />
        <Skeleton className="h-4 w-2/3" variant="default" wave={waveIndex} />
      </div>

      {/* 선택지 */}
      <div className="space-y-3">
        <Skeleton className="h-12 w-full rounded-lg" variant="glow" wave={waveIndex} />
        <Skeleton className="h-12 w-full rounded-lg" variant="glow" wave={waveIndex} />
        <Skeleton className="h-12 w-full rounded-lg" variant="glow" wave={waveIndex} />
      </div>

      {/* 하단 통계 */}
      {showStats && (
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-16" variant="multi" wave={waveIndex} />
            <Skeleton className="h-4 w-20" variant="multi" wave={waveIndex} />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-20 rounded" variant="default" wave={waveIndex} />
            <Skeleton className="h-9 w-16 rounded" variant="default" wave={waveIndex} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 설문 리스트 스켈레톤
 */
interface QuestionListSkeletonProps {
  count?: number;
  className?: string;
}

function QuestionListSkeleton({ count = 3, className }: QuestionListSkeletonProps) {
  return (
    <div className={cn('space-y-6', className)}>
      {Array.from({ length: count }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <QuestionCardSkeleton key={i} index={i} />
      ))}
    </div>
  );
}

/**
 * 사용자 통계 카드 스켈레톤
 */
interface UserStatsCardSkeletonProps {
  index?: number;
}

function UserStatsCardSkeleton({ index = 0 }: UserStatsCardSkeletonProps) {
  const waveIndex = (index % 6) + 1;

  return (
    <div className="bg-white border-0 shadow-sm rounded-xl p-6 space-y-4 shimmer-container">
      <div className="flex items-center justify-between">
        <Skeleton className="w-12 h-12 rounded-xl" variant="glow" wave={waveIndex} />
        <Skeleton className="h-6 w-12 rounded" variant="multi" wave={waveIndex} />
      </div>
      <Skeleton className="h-8 w-12" variant="wave" wave={waveIndex} />
      <Skeleton className="h-4 w-24" variant="default" wave={waveIndex} />
    </div>
  );
}

/**
 * 프로필 헤더 스켈레톤
 */
function ProfileHeaderSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 shimmer-container">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Skeleton className="w-16 h-16 rounded-2xl" variant="glow" wave={1} />
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" variant="wave" wave={1} />
            <Skeleton className="h-5 w-64" variant="default" wave={1} />
          </div>
        </div>
        <Skeleton className="h-10 w-24 rounded" variant="multi" wave={1} />
      </div>
    </div>
  );
}

/**
 * 댓글 섹션 스켈레톤
 */
interface CommentSkeletonProps {
  index?: number;
}

function CommentSkeleton({ index = 0 }: CommentSkeletonProps) {
  const waveIndex = (index % 6) + 1;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" variant="glow" wave={waveIndex} />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-16" variant="default" wave={waveIndex} />
            <Skeleton className="h-3 w-12" variant="default" wave={waveIndex} />
          </div>
          <Skeleton className="h-4 w-full" variant="wave" wave={waveIndex} />
          <Skeleton className="h-4 w-3/4" variant="wave" wave={waveIndex} />
        </div>
      </div>
    </div>
  );
}

interface CommentsSkeletonProps {
  count?: number;
}

function CommentsSkeleton({ count = 2 }: CommentsSkeletonProps) {
  return (
    <div className="space-y-4">
      <Skeleton className="h-6 w-32" variant="multi" wave={1} />
      {Array.from({ length: count }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <CommentSkeleton key={i} index={i} />
      ))}
    </div>
  );
}

/**
 * 테이블 로우 스켈레톤
 */
interface TableRowSkeletonProps {
  columns?: number;
  index?: number;
}

function TableRowSkeleton({ columns = 4, index = 0 }: TableRowSkeletonProps) {
  const waveIndex = (index % 6) + 1;

  return (
    <div className="flex items-center gap-4 p-4 border-b border-border">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className="h-4 flex-1"
          variant={i === 0 ? 'glow' : 'default'}
          wave={waveIndex}
        />
      ))}
    </div>
  );
}

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

function TableSkeleton({ rows = 5, columns = 4 }: TableSkeletonProps) {
  return (
    <div className="border border-border rounded-lg overflow-hidden shimmer-container">
      {/* 헤더 */}
      <div className="bg-muted/50 border-b border-border">
        <div className="flex items-center gap-4 p-4">
          {Array.from({ length: columns }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton key={i} className="h-5 flex-1" variant="multi" wave={1} />
          ))}
        </div>
      </div>
      {/* 로우들 */}
      {Array.from({ length: rows }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableRowSkeleton key={i} columns={columns} index={i} />
      ))}
    </div>
  );
}

/**
 * 인라인 로딩 컨테이너
 */
interface InlineLoadingProps {
  isLoading: boolean;
  children: React.ReactNode;
  skeleton: React.ReactNode;
  className?: string;
}

function InlineLoading({ isLoading, children, skeleton, className }: InlineLoadingProps) {
  return (
    <div className={cn('relative', className)}>
      {isLoading ? (
        <div className="animate-fade-in">{skeleton}</div>
      ) : (
        <div className="slide-in-from-bottom">{children}</div>
      )}
    </div>
  );
}

/**
 * 섹션 로딩 (제목 + 컨텐츠)
 */
interface SectionLoadingProps {
  isLoading: boolean;
  title?: string;
  children: React.ReactNode;
  skeleton: React.ReactNode;
  className?: string;
}

function SectionLoading({ isLoading, title, children, skeleton, className }: SectionLoadingProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              <span>로딩중...</span>
            </div>
          )}
        </div>
      )}
      <InlineLoading isLoading={isLoading} skeleton={skeleton}>
        {children}
      </InlineLoading>
    </div>
  );
}

/**
 * 그리드 스켈레톤 (반응형)
 */
interface GridSkeletonProps {
  count?: number;
  columns?: {
    default: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  variant?: 'card' | 'list' | 'stats';
  className?: string;
}

function GridSkeleton({
  count = 6,
  columns = { default: 1, md: 2, lg: 3 },
  variant = 'card',
  className,
}: GridSkeletonProps) {
  const getGridClasses = () => {
    const { default: def, sm, md, lg, xl } = columns;
    return cn(
      `grid gap-6 grid-cols-${def}`,
      sm && `sm:grid-cols-${sm}`,
      md && `md:grid-cols-${md}`,
      lg && `lg:grid-cols-${lg}`,
      xl && `xl:grid-cols-${xl}`,
    );
  };

  const renderSkeletonItem = (index: number) => {
    switch (variant) {
      case 'card':
        return <QuestionCardSkeleton key={index} index={index} />;
      case 'stats':
        return <UserStatsCardSkeleton key={index} index={index} />;
      case 'list':
      default:
        return (
          <div key={index} className="space-y-3 p-4 bg-white rounded-lg border shimmer-container">
            <Skeleton className="h-4 w-3/4" variant="wave" wave={(index % 6) + 1} />
            <Skeleton className="h-4 w-1/2" variant="default" wave={(index % 6) + 1} />
            <Skeleton className="h-4 w-2/3" variant="glow" wave={(index % 6) + 1} />
          </div>
        );
    }
  };

  return (
    <div className={cn(getGridClasses(), className)}>
      {Array.from({ length: count }).map((_, i) => renderSkeletonItem(i))}
    </div>
  );
}

export {
  Skeleton,
  QuestionCardSkeleton,
  QuestionListSkeleton,
  UserStatsCardSkeleton,
  ProfileHeaderSkeleton,
  CommentSkeleton,
  CommentsSkeleton,
  TableRowSkeleton,
  TableSkeleton,
  InlineLoading,
  SectionLoading,
  GridSkeleton,
};
